import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';

import StickyFooter from '../components/StickyFooter';
import CollapsibleSection from '../components/CollapsibleSection';
import { uploadData, getUrl, list } from 'aws-amplify/storage';

interface Course {
  id: string;
  courseId?: string; // Admin-managed ID field
  title: string;
  punchline?: string; // Course punchline
  shortDescription?: string; // Course short description
  description?: string;
  price: string;
  instructor?: string; // This will now store the tutor ID
  instructor2?: string; // Second tutor ID (optional)
  location?: string;
  startDate?: string;
  endDate?: string;
  targetgroup?: string;
  urlSlug?: string; // Admin-defined URL slug for course pages
  partnerId?: string;
  partner2Id?: string;
  heroImagePath?: string;
  isExternalRegistration?: boolean;
  externalRegistrationUrl?: string;
  isActive: boolean;
  discountInformation?: string;
}

interface Session {
  id?: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  locationType: 'ONLINE' | 'ON_SITE';
  location: string;
  description?: string;
  maxAttendees?: number;
  order?: number; // Session order/sequence
  isActive: boolean;
}

interface CourseVideo {
  id?: string;
  url: string;
  heading: string;
  isPrimary: boolean; // true => left column, false => right column
  order?: number;
  isActive: boolean;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

interface AdminCourseEditorProps {
  selectedCourseId?: string;
  onBack?: () => void;
}

const AdminCourseEditor: React.FC<AdminCourseEditorProps> = ({ selectedCourseId: propSelectedCourseId, onBack }) => {
  const navigate = useNavigate();
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [availableCourses, setAvailableCourses] = useState<{ id: string; courseId: string; title: string; location: string; instructor?: string }[]>([]);
  const [availablePartners, setAvailablePartners] = useState<{ id: string; name: string }[]>([]);
  const [availableTutors, setAvailableTutors] = useState<{ id: string; name: string; company: string }[]>([]);
  
  const [course, setCourse] = useState<Partial<Course>>({
    courseId: '',
    title: '',
    punchline: '',
    shortDescription: '',
    description: '',
    price: '',
    instructor: '',
    instructor2: '',
    location: '',
    startDate: '',
    endDate: '',
    targetgroup: '',
    urlSlug: '',
    partnerId: '',
    partner2Id: '',
    heroImagePath: '',
    isExternalRegistration: false,
    externalRegistrationUrl: '',
    isActive: true,
    discountInformation: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string>('');
  const [heroUploading, setHeroUploading] = useState<boolean>(false);
  const [existingHeroItems, setExistingHeroItems] = useState<{ key: string; label: string }[]>([]);
  const [loadingExistingHeroes, setLoadingExistingHeroes] = useState<boolean>(false);
  
  // Sessions management
  const [sessions, setSessions] = useState<Session[]>([]);
  const [numberOfSessions, setNumberOfSessions] = useState<number>(1);
  const [currentSessionIndex, setCurrentSessionIndex] = useState<number>(0);
  // Videos management
  const [videos, setVideos] = useState<CourseVideo[]>([]);
  
  // Collapsible section states
  const [collapsibleStates, setCollapsibleStates] = useState({
    description: false,
    settings: false,
    media: false,
    locationAndDates: false,
    sessions: false
  });

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'align'
  ];

  const isEditMode = Boolean(selectedCourseId);
  // Enable secondary tutor support
  const supportsTutor2 = true;

  // Helper function to toggle collapsible sections
  const toggleCollapsibleSection = (section: keyof typeof collapsibleStates) => {
    setCollapsibleStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handler for Quill editor changes
  const handleDescriptionChange = (content: string) => {
    setCourse(prev => ({
      ...prev,
      description: content
    }));
  };

  const handleDiscountChange = (content: string) => {
    setCourse(prev => ({
      ...prev,
      discountInformation: content
    }));
  };

  useEffect(() => {
    console.log('🔒 AdminCourseEditor: Component mounted');
    loadAvailableCourses();
    loadAvailablePartners();
    loadAvailableTutors();
    updateSessionsCount(1); // Initialize with one session
    
    // Cleanup function to log unmounting
    return () => {
      console.log('🔒 AdminCourseEditor: Component unmounting, selectedCourseId was:', selectedCourseId);
    };
  }, []);

  useEffect(() => {
    if (propSelectedCourseId) {
      console.log('🔒 AdminCourseEditor: Loading course with prop ID:', propSelectedCourseId);
      setSelectedCourseId(propSelectedCourseId);
      loadCourse(propSelectedCourseId);
    }
  }, [propSelectedCourseId]);

  // Build preview URL for hero image when path changes
  useEffect(() => {
    (async () => {
      const path = course.heroImagePath || '';
      if (!path) {
        setHeroPreviewUrl('');
        return;
      }
      try {
        // If absolute URL or absolute path in app, use directly
        if (path.startsWith('http') || path.startsWith('/')) {
          setHeroPreviewUrl(path);
          return;
        }
        // Otherwise, treat as S3 object key under public access level
        const { url } = await getUrl({ path: `public/${path}` });
        setHeroPreviewUrl(url.toString());
      } catch (e) {
        console.warn('Hero preview URL generation failed:', e);
        setHeroPreviewUrl('');
      }
    })();
  }, [course.heroImagePath]);

  // Load existing hero images from S3 (public/courses/...)
  const loadExistingHeroImages = async () => {
    try {
      setLoadingExistingHeroes(true);
      const items: { key: string; label: string }[] = [];
      // Top-level list of all hero images (public level)
      const { items: listed } = await list({ path: 'public/courses/' });
      for (const it of listed || []) {
        if (!it?.path) continue;
        // it.path includes the 'public/' prefix; strip it for storing on Course
        const keyWithPublic = (it.path || '').replace(/^\//, '');
        const displayKey = keyWithPublic.startsWith('public/') ? keyWithPublic.substring('public/'.length) : keyWithPublic;
        // Only include files (skip folders)
        if (displayKey.endsWith('/') === false) {
          const fileName = displayKey.split('/').pop() || displayKey;
          items.push({ key: displayKey, label: `${fileName}` });
        }
      }
      // Sort by label for now
      items.sort((a, b) => a.label.localeCompare(b.label));
      setExistingHeroItems(items);
    } catch (e) {
      console.warn('Failed to list existing hero images:', e);
      setExistingHeroItems([]);
    } finally {
      setLoadingExistingHeroes(false);
    }
  };

  useEffect(() => {
    loadExistingHeroImages();
  }, []);

  // Inject CSS styles for sessions
  useEffect(() => {
    const styles = `
      .form-section {
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid #e5e7eb;
      }

      .form-section h3 {
        color: #008F99;
        margin-bottom: 1.5rem;
        font-size: 1.25rem;
        font-weight: 600;
      }

      .session-management {
        margin-bottom: 2rem;
      }

      .session-header-controls {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: #f8f9fa;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
      }

      .session-count-selector {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        white-space: nowrap;
      }

      .session-count-selector label {
        font-weight: 500;
        color: #374151;
        font-size: 0.9rem;
      }

      .session-count-selector select {
        padding: 0.4rem 0.8rem;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        background: white;
        color: #374151;
        font-size: 0.9rem;
        min-width: 60px;
      }

      .session-tiles {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        align-items: center;
      }

      .session-tile {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.85rem;
        font-weight: 600;
      }

      .session-tile:hover {
        background: #f3f4f6;
        border-color: #008F99;
      }

      .session-tile.active {
        background: #008F99;
        color: white;
        border-color: #008F99;
      }

      @media (max-width: 768px) {
        .session-header-controls {
          flex-direction: column;
          align-items: stretch;
          gap: 1rem;
        }
        
        .session-count-selector {
          justify-content: space-between;
        }
        
        .session-tiles {
          justify-content: center;
        }
      }

      .session-editor {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 1.5rem;
      }

      .session-header {
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #008F99;
      }

      .session-header h4 {
        margin: 0;
        color: #374151;
        font-size: 1.2rem;
        font-weight: 600;
      }

      .copy-btn {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1rem;
        padding: 0.2rem 0.5rem;
        margin-left: 0.5rem;
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      .copy-btn:hover {
        background: #f3f4f6;
      }

      .copy-btn:active {
        background: #e5e7eb;
      }

      .session-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        margin-bottom: 1rem;
      }

      @media (max-width: 768px) {
        .session-row {
          grid-template-columns: 1fr;
        }
      }

      .session-form .form-field {
        margin-bottom: 1rem;
      }

      .session-form label {
        font-weight: 500;
        color: #374151;
        margin-bottom: 0.25rem;
      }

      .session-form input,
      .session-form select,
      .session-form textarea {
        border: 1px solid #d1d5db;
        border-radius: 4px;
        padding: 0.5rem;
        font-size: 0.9rem;
      }

      .session-form input:focus,
      .session-form select:focus,
      .session-form textarea:focus {
        outline: none;
        border-color: #008F99;
        box-shadow: 0 0 0 2px rgba(0, 143, 153, 0.1);
      }

      /* Sessions Preview Styles */
      .sessions-preview {
        background: #f8f9fa;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 1.5rem;
        margin-top: 1rem;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }

      .sessions-preview h4 {
        color: #374151;
        margin-bottom: 1rem;
        font-size: 1.1rem;
        font-weight: 600;
      }

      .sessions-detailed {
        margin-top: 1rem;
      }

      .sessions-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .session-card {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .session-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
        flex-wrap: wrap;
      }

      .session-number {
        background: #008F99;
        color: white;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: 600;
        min-width: 60px;
        text-align: center;
      }

      .session-header h4 {
        margin: 0;
        color: #374151;
        font-size: 1rem;
        font-weight: 600;
        flex: 1;
      }

      .session-type {
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .session-type.online {
        background: #dbeafe;
        color: #1e40af;
      }

      .session-type.on_site {
        background: #dcfce7;
        color: #166534;
      }

      .session-details {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 0.75rem;
        margin-bottom: 0.75rem;
        font-size: 0.9rem;
      }

      .session-details > div {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .session-details strong {
        color: #374151;
        font-weight: 600;
      }

      .session-description {
        border-top: 1px solid #e5e7eb;
        padding-top: 0.75rem;
        margin-top: 0.75rem;
      }

      .session-description p {
        margin: 0;
        color: #6b7280;
        font-size: 0.9rem;
        line-height: 1.4;
      }

      @media (max-width: 768px) {
        .session-details {
          grid-template-columns: 1fr;
        }
        
        .session-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }
      }

      /* Ensure the editor card allows sticky children */
      .admin-content-card.admin-course-editor {
        overflow: visible;
        display: flex;
        flex-direction: column;
      }

      /* Ensure enough space for fixed actions */
      .admin-course-editor form {
        padding-bottom: 96px !important;
        flex: 1;
        min-height: 0;
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    if (!document.head.querySelector('style[data-sessions-styles]')) {
      styleElement.setAttribute('data-sessions-styles', 'true');
      document.head.appendChild(styleElement);
    }
    
    return () => {
      // Cleanup on component unmount
      const existingStyle = document.head.querySelector('style[data-sessions-styles]');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const loadAvailableCourses = async () => {
    try {
      console.log('🔍 Loading available courses...');
      const { data } = await client.models.Course.list();
      console.log('📝 Available courses loaded:', data);
      
      const courses = (data || []).map((course: any) => ({
        id: course.id,
        courseId: course.courseId || 'No ID',
        title: course.title || 'Untitled Course',
        location: course.location || 'No Location',
        // store tutorId into our local 'instructor' field used by the UI
        instructor: (course as any).tutorId || ''
      }));
      
      setAvailableCourses(courses);
    } catch (error) {
      console.error('❌ Error loading available courses:', error);
      setAvailableCourses([]);
    }
  };

  const loadAvailablePartners = async () => {
    try {
      console.log('🔍 Loading available partners...');
      const { data } = await client.models.Partner.list();
      console.log('📝 Available partners loaded:', data);
      
      const partners = (data || []).map((partner: any) => ({
        id: partner.id,
        name: partner.name || 'Unnamed Partner'
      }));
      
      setAvailablePartners(partners);
    } catch (error) {
      console.error('❌ Error loading available partners:', error);
      setAvailablePartners([]);
    }
  };

  const loadAvailableTutors = async () => {
    try {
      console.log('🔍 Loading available tutors...');
      const { data } = await client.models.Tutor.list();
      console.log('📝 Available tutors loaded:', data);
      
      const tutors = (data || []).map((tutor: any) => ({
        id: tutor.id,
        name: tutor.name || 'Unnamed Tutor',
        company: tutor.company || 'No Company'
      }));
      
      setAvailableTutors(tutors);
    } catch (error) {
      console.error('❌ Error loading available tutors:', error);
      setAvailableTutors([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setCourse(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const loadCourse = async (courseId: string) => {
    setLoading(true);
    try {
      console.log('🔍 Loading course with ID:', courseId);
      const { data } = await client.models.Course.get({ 
        id: courseId
      });
      console.log('📝 Course loaded:', JSON.stringify(data, null, 2));
      console.log('🔍 URL Slug from data:', (data as any)?.urlSlug);
      console.log('🔍 All course fields:', Object.keys(data || {}));
      console.log('🔍 Raw urlSlug field type:', typeof (data as any)?.urlSlug);
      console.log('🔍 urlSlug field exists:', 'urlSlug' in (data || {}));
      
      if (data) {
        const courseData = {
          id: data.id,
          courseId: data.courseId || '',
          title: data.title || '',
          punchline: (data as any).punchline || '',
          shortDescription: (data as any).shortDescription || '',
          description: data.description || '',
          price: data.price || '',
          // Map backend tutorId into UI 'instructor' field (stores tutor ID)
          instructor: (data as any).tutorId || '',
          instructor2: (data as any).tutor2Id || '',
          location: data.location || '',
          urlSlug: (data as any).urlSlug || '',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          targetgroup: data.targetgroup || '',
          partnerId: data.partnerId || '',
          partner2Id: (data as any).partner2Id || '',
          heroImagePath: (data as any).heroImagePath || '',
          isExternalRegistration: (data as any).isExternalRegistration ?? false,
          externalRegistrationUrl: (data as any).externalRegistrationUrl || '',
          isActive: data.isActive ?? true,
          discountInformation: (data as any).discountInformation || '',
        };
        console.log('🔍 Setting course state with urlSlug:', courseData.urlSlug);
        console.log('🔍 Full course data being set:', JSON.stringify(courseData, null, 2));
        setCourse(courseData);
        setMessage(null);
        
        // Load sessions for this course
        await loadCourseSessions(courseId);
        // Load videos for this course
        await loadCourseVideos(courseId);
      }
    } catch (error) {
      console.error('❌ Error loading course:', error);
      setMessage({ type: 'error', text: 'Failed to load course. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelection = (courseId: string) => {
    console.log('🔒 Course selection changed to:', courseId);
    
    // Clear any existing state first to prevent contamination
    setMessage(null);
    
    if (courseId) {
      // Set the selected course ID first
      setSelectedCourseId(courseId);
      // Load the course data
      loadCourse(courseId);
    } else {
      // Reset to create mode - clear all state
      console.log('🔒 Resetting to create mode');
      setSelectedCourseId('');
      setCourse({
        courseId: '',
        title: '',
        punchline: '',
        shortDescription: '',
        description: '',
        price: '',
        instructor: '',
        instructor2: '',
        location: '',
        startDate: '',
        endDate: '',
        targetgroup: '',
        partnerId: '',
        partner2Id: '',
        heroImagePath: '',
        isExternalRegistration: false,
        externalRegistrationUrl: '',
        isActive: true,
        discountInformation: '',
      });
      // Reset sessions
      setSessions([]);
      setNumberOfSessions(1);
      setCurrentSessionIndex(0);
      // Reset videos
      setVideos([]);
    }
  };

  // CRITICAL SAFETY FUNCTION: Validate course data before submission
  const validateCourseSubmission = (): { isValid: boolean; error: string | null } => {
    if (isEditMode) {
      if (!selectedCourseId) {
        return { isValid: false, error: 'CRITICAL ERROR: No course ID selected for editing' };
      }
      if (!course.title?.trim()) {
        return { isValid: false, error: 'Course title is required' };
      }
      if (!course.price?.trim()) {
        return { isValid: false, error: 'Course price is required' };
      }
    } else {
      if (!course.title?.trim()) {
        return { isValid: false, error: 'Course title is required' };
      }
      if (!course.price?.trim()) {
        return { isValid: false, error: 'Course price is required' };
      }
    }
    return { isValid: true, error: null };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }
    
    setSaving(true);
    setMessage(null);

    try {
      // CRITICAL SAFETY CHECK: Validate before proceeding
      const validation = validateCourseSubmission();
      if (!validation.isValid) {
        throw new Error(validation.error || 'Unknown validation error');
      }

      // Try apiKey auth mode first, then userPool if needed
      const client = generateClient<Schema>({ authMode: 'userPool' });
      
      // Add detailed logging for debugging
      console.log('🔧 Course submission started');
      console.log('🔍 Auth mode: apiKey');
      console.log('🔍 Environment:', process.env.NODE_ENV);
      console.log('🔍 Current course state:', JSON.stringify(course, null, 2));
      console.log('🔍 Discount information value:', course.discountInformation);

      if (isEditMode) {
        // CRITICAL SAFETY CHECK: Ensure we're editing the correct course
        if (!selectedCourseId) {
          throw new Error('CRITICAL ERROR: No course ID selected for editing. This could cause data corruption.');
        }
        
        console.log('🔒 SAFETY CHECK: Editing course with ID:', selectedCourseId);
        console.log('🔒 Current course state ID:', course.id);
        console.log('🔒 Selected course ID:', selectedCourseId);
        
        // Update existing course
        const updatePayload: any = {
          id: selectedCourseId,
          courseId: course.courseId,
          title: course.title,
          punchline: course.punchline || undefined,
          shortDescription: course.shortDescription || undefined,
          description: course.description,
          price: course.price,
          // Backend schema expects tutorId, map from UI 'instructor'
          tutorId: course.instructor || undefined,
          tutor2Id: course.instructor2 || undefined,
          location: course.location,
          urlSlug: course.urlSlug,
          startDate: course.startDate || undefined,
          endDate: course.endDate || undefined,
          targetgroup: course.targetgroup,
          partnerId: course.partnerId,
          partner2Id: course.partner2Id,
          heroImagePath: course.heroImagePath || undefined,
          isExternalRegistration: !!course.isExternalRegistration,
          externalRegistrationUrl: course.isExternalRegistration ? (course.externalRegistrationUrl || '') : undefined,
          isActive: course.isActive,
          discountInformation: course.discountInformation || undefined,
        };
        console.log('🔎 Update payload:', updatePayload);
        
        // CRITICAL SAFETY CHECK: Verify we're not accidentally updating the wrong course
        if (course.id && course.id !== selectedCourseId) {
          console.error('🚨 CRITICAL WARNING: Course ID mismatch detected!');
          console.error('🚨 course.id:', course.id);
          console.error('🚨 selectedCourseId:', selectedCourseId);
          console.error('🚨 This could cause data corruption. Proceeding with selectedCourseId for safety.');
        }
        
        const result = await client.models.Course.update(updatePayload);
        console.log('Course update result:', result);
        // Save sessions for this course
        try {
          await saveSessions(selectedCourseId);
        } catch (sessionError) {
          console.warn('⚠️ Failed to save sessions, but course was updated:', sessionError);
        }
        // Save videos for this course
        try {
          await saveVideos(selectedCourseId);
        } catch (videoError) {
          console.warn('⚠️ Failed to save videos, but course was updated:', videoError);
        }
        
        // Refresh course from backend to reflect saved state (including tutor2Id)
        try {
          await loadCourse(selectedCourseId);
        } catch (refreshError) {
          console.warn('⚠️ Failed to refresh course after update:', refreshError);
        }
        setMessage({ type: 'success', text: 'Course updated successfully!' });
      } else {
        // Create new course - include all fields
        const courseData: any = {
          courseId: course.courseId || '',
          title: course.title || '',
          punchline: course.punchline || undefined,
          shortDescription: course.shortDescription || undefined,
          description: course.description || '',
          price: course.price || '',
          // Backend schema expects tutorId, map from UI 'instructor'
          tutorId: course.instructor || undefined,
          tutor2Id: course.instructor2 || undefined,
          location: course.location || '',
          urlSlug: course.urlSlug || '',
          startDate: course.startDate || undefined,
          endDate: course.endDate || undefined,
          targetgroup: course.targetgroup || '',
          partnerId: course.partnerId || undefined,
          partner2Id: course.partner2Id || undefined,
          heroImagePath: course.heroImagePath || undefined,
          isExternalRegistration: !!course.isExternalRegistration,
          externalRegistrationUrl: course.isExternalRegistration ? (course.externalRegistrationUrl || '') : undefined,
          isActive: true,
          discountInformation: course.discountInformation || undefined
        };
        
        console.log('📝 Creating course with data:', courseData);
        console.log('📝 Required fields check:', {
          title: !!courseData.title,
          price: !!courseData.price
        });
        
        const result = await client.models.Course.create(courseData);
        console.log('✅ Course creation result:', result);
        
        if (result.errors && result.errors.length > 0) {
          console.error('❌ GraphQL errors in result:', result.errors);
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }
        // Save sessions for the newly created course
        if (result.data?.id) {
          try {
            await saveSessions(result.data.id);
          } catch (sessionError) {
            console.warn('⚠️ Failed to save sessions, but course was created:', sessionError);
          }
          try {
            await saveVideos(result.data.id);
          } catch (videoError) {
            console.warn('⚠️ Failed to save videos, but course was created:', videoError);
          }
        }
        
        setMessage({ type: 'success', text: 'Course created successfully!' });
        
        // Reset form
        setCourse({
          courseId: '',
          title: '',
          punchline: '',
          shortDescription: '',
          description: '',
          price: '',
          instructor: '',
          instructor2: '',
          location: '',
          startDate: '',
          endDate: '',
          targetgroup: '',
          partnerId: '',
          partner2Id: '',
          heroImagePath: '',
          isExternalRegistration: false,
          externalRegistrationUrl: '',
          isActive: true,
          discountInformation: '',
        });
        
        // Reset sessions
        updateSessionsCount(1);
        // Reset videos
        setVideos([]);
        
        // Reload available courses list
        await loadAvailableCourses();
      }
    } catch (err) {
      console.error('❌ Error saving course:', err);
      console.error('❌ Error details:', {
        name: (err as any)?.name,
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        graphQLErrors: (err as any)?.errors
      });
      
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} course.`;
      
      if ((err as any)?.message?.includes('Authorization')) {
        errorMessage += ' Authorization error - please ensure you are logged in as an admin.';
      } else if ((err as any)?.message?.includes('GraphQL')) {
        errorMessage += ` GraphQL error: ${(err as any).message}`;
      } else {
        errorMessage += ` Error: ${(err as any)?.message || 'Unknown error'}`;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // Initialize sessions when numberOfSessions changes
  const updateSessionsCount = (count: number) => {
    console.log(`🔢 Updating session count from ${sessions.length} to ${count}`);
    setNumberOfSessions(count);
    const currentSessions = [...sessions];
    
    if (count > currentSessions.length) {
      // Add new empty sessions
      for (let i = currentSessions.length; i < count; i++) {
        currentSessions.push({
          title: `Session ${i + 1}`,
          date: '',
          startTime: '09:00',
          endTime: '17:00',
          locationType: 'ON_SITE',
          location: '',
          description: '',
          maxAttendees: 20,
          order: i, // Set order based on index
          isActive: true
        });
      }
      console.log(`➕ Added ${count - sessions.length} new sessions`);
    } else if (count < currentSessions.length) {
      // Remove excess sessions
      const removedCount = currentSessions.length - count;
      currentSessions.splice(count);
      console.log(`➖ Removed ${removedCount} sessions`);
    }
    
    setSessions(currentSessions);
    // Reset to first session when count changes
    setCurrentSessionIndex(0);
    
    // Show message to user that they need to save to persist changes
    setMessage({ 
      type: 'success', 
      text: `Session count changed to ${count}. Remember to save the course to persist these changes.` 
    });
  };

  // Update a specific session
  const updateSession = (index: number, field: keyof Session, value: any) => {
    const updatedSessions = [...sessions];
    updatedSessions[index] = {
      ...updatedSessions[index],
      [field]: value
    };
    
    // Update order field when sessions are reordered
    if (field === 'order' || field === 'title' || field === 'date') {
      updatedSessions.forEach((session, idx) => {
        session.order = idx;
      });
    }
    
    setSessions(updatedSessions);
  };

  // Copy field from previous session
  const copyFromPreviousSession = (field: keyof Session) => {
    const prevIndex = currentSessionIndex - 1;
    if (prevIndex >= 0 && prevIndex < sessions.length) {
      const prevSession = sessions[prevIndex];
      if (prevSession[field] !== undefined) {
        updateSession(currentSessionIndex, field, prevSession[field]);
      }
    }
  };

  // Load sessions for a course
  const loadCourseSessions = async (courseId: string) => {
    try {
      // Check if Session model is available
      console.log('🔍 Available models:', Object.keys(client.models || {}));
      if (!(client.models as any).Session) {
        console.warn('⚠️ Session model not available, skipping session load');
        return;
      }

      // Fetch all sessions for this course (with explicit limit to avoid pagination)
      const { data } = await (client.models as any).Session.list({
        filter: { courseId: { eq: courseId } },
        limit: 1000 // Explicit high limit to get all sessions
      });
      
      console.log(`📊 Fetched ${data?.length || 0} total sessions for course ${courseId}`);
      
      // CRITICAL: Filter out soft-deleted sessions in JavaScript
      // (status field not available in GraphQL filter API)
      const activeSessions = (data || []).filter((session: any) => {
        const isDeleted = session.status === 'deleted';
        if (isDeleted) {
          console.log(`🗑️ Filtering out deleted session: ${session.id}`);
        }
        return !isDeleted;
      });
      
      console.log(`✅ Found ${activeSessions.length} active sessions`);
      
      if (activeSessions.length > 0) {
        const courseSessions = activeSessions
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0)) // Sort by order
          .map((session: any) => ({
            id: session.id,
            title: session.title || '',
            date: session.date || '',
            startTime: session.startTime || '09:00',
            endTime: session.endTime || '17:00',
            locationType: (session.locationType || 'ON_SITE') as 'ONLINE' | 'ON_SITE',
            location: session.location || '',
            description: session.description || '',
            maxAttendees: session.maxAttendees || 20,
            order: session.order || 0, // Include order field
            isActive: session.isActive ?? true
          }));
        
        setSessions(courseSessions);
        setNumberOfSessions(courseSessions.length);
        setCurrentSessionIndex(0);
      } else {
        // Initialize with one empty session
        updateSessionsCount(1);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      updateSessionsCount(1); // Fallback to one session
    }
  };

  // Save sessions to database
  const saveSessions = async (courseId: string) => {
    try {
      // Check if Session model is available
      if (!(client.models as any).Session) {
        console.warn('⚠️ Session model not available, skipping session save');
        return;
      }

      // Fetch all existing sessions for this course (with explicit limit to avoid pagination)
      const { data: allSessions } = await (client.models as any).Session.list({
        filter: { courseId: { eq: courseId } },
        limit: 1000 // Explicit high limit to get all sessions
      });
      
      // SOFT DELETE existing active sessions first (data immutability)
      // Filter in JavaScript to get only active sessions
      const existingSessions = (allSessions || []).filter((s: any) => s.status !== 'deleted');
      
      console.log(`🗑️ Soft-deleting ${existingSessions.length} existing active sessions`);
      
      if (existingSessions && existingSessions.length > 0) {
        for (const session of existingSessions) {
          // Soft delete: mark as deleted instead of hard delete
          await (client.models as any).Session.update({ 
            id: session.id,
            status: 'deleted',
            deletedAt: new Date().toISOString()
          });
        }
      }
      
      // Create new sessions - save ALL sessions to preserve the count
      for (let i = 0; i < sessions.length; i++) {
        const session = sessions[i];
        
        // Use default values for empty sessions to ensure they're saved
        const sessionData = {
          courseId,
          title: session.title || `Session ${i + 1}`,
          date: session.date || '', // Allow empty date for now
          startTime: session.startTime || '09:00',
          endTime: session.endTime || '17:00',
          locationType: session.locationType || 'ON_SITE',
          location: session.location || '',
          description: session.description || '',
          maxAttendees: session.maxAttendees || 20,
          order: i, // Set order based on array index
          isActive: session.isActive ?? true,
          status: 'active' // Explicitly set status to active for new sessions
        };
        
        console.log(`💾 Saving session ${i + 1}:`, sessionData);
        const createResult = await (client.models as any).Session.create(sessionData);
        console.log(`✅ Session ${i + 1} created with ID:`, createResult?.data?.id);
      }
    } catch (error) {
      console.error('Error saving sessions:', error);
      throw error;
    }
  };

  // Load videos for a course
  const loadCourseVideos = async (courseId: string) => {
    try {
      if (!(client.models as any).CourseVideo) {
        console.warn('⚠️ CourseVideo model not available, skipping video load');
        return;
      }
      // Fetch all videos for this course (with explicit limit to avoid pagination)
      const { data } = await (client.models as any).CourseVideo.list({
        filter: { courseId: { eq: courseId } },
        limit: 100 // Explicit high limit (max 10 videos per course but fetch more for safety)
      });
      
      // Filter out soft-deleted videos in JavaScript (status field not in GraphQL filter API)
      const activeVideos = (data || []).filter((v: any) => v && v.status !== 'deleted');
      
      if (activeVideos && activeVideos.length > 0) {
        const courseVideos: CourseVideo[] = activeVideos
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
          .map((v: any) => ({
            id: v.id,
            url: v.url || '',
            heading: v.heading || '',
            isPrimary: (v.isPrimary ?? true) as boolean,
            order: v.order || 0,
            isActive: v.isActive ?? true,
          }));
        setVideos(courseVideos.slice(0, 10));
      } else {
        setVideos([]);
      }
    } catch (error) {
      console.error('Error loading course videos:', error);
      setVideos([]);
    }
  };

  // Save videos to database (up to 10)
  const saveVideos = async (courseId: string) => {
    try {
      if (!(client.models as any).CourseVideo) {
        console.warn('⚠️ CourseVideo model not available, skipping video save');
        return;
      }
      // If no videos are defined in the UI, do not modify existing backend videos.
      // This prevents accidental deletion when saving unrelated course fields.
      if (!videos || videos.length === 0) {
        console.log('ℹ️ No videos in editor state; skipping video updates to preserve existing data');
        return;
      }
      // Fetch all existing videos for this course (with explicit limit to avoid pagination)
      const { data: allVideos } = await (client.models as any).CourseVideo.list({
        filter: { courseId: { eq: courseId } },
        limit: 100 // Explicit high limit (max 10 videos per course but fetch more for safety)
      });
      
      // Filter in JavaScript to get only active videos
      const existing = (allVideos || []).filter((v: any) => v && v.status !== 'deleted');
      
      if (existing && existing.length > 0) {
        for (const v of existing) {
          try {
            // SOFT DELETE: mark as deleted instead of hard delete (data immutability)
            await (client.models as any).CourseVideo.update({ 
              id: v.id,
              status: 'deleted',
              deletedAt: new Date().toISOString()
            });
          } catch (delErr) {
            console.warn('Failed to soft delete CourseVideo', v?.id, delErr);
          }
        }
      }
      // Create new up to 10 - de-duplicate by normalized URL (case/space-insensitive)
      const seen = new Set<string>();
      const toSave = videos
        .slice(0, 10)
        .filter((v) => {
          const key = (v.url || '').trim().toLowerCase();
          if (!key) return true; // allow empty to be skipped later
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      for (let i = 0; i < toSave.length; i++) {
        const v = toSave[i];
        const payload = {
          courseId,
          url: (v.url || '').trim(),
          heading: (v.heading || '').trim(),
          isPrimary: !!v.isPrimary,
          order: i,
          isActive: v.isActive ?? true,
        };
        // Skip creating completely empty entries
        if (!payload.url && !payload.heading) continue;
        console.log(`💾 Saving course video ${i + 1}:`, payload);
        await (client.models as any).CourseVideo.create(payload);
      }
    } catch (error) {
      console.error('Error saving course videos:', error);
      throw error;
    }
  };

  // Video helpers
  const extractYouTubeEmbedUrl = (rawUrl: string): string => {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl);
      if (url.hostname.includes('youtu.be')) {
        const id = url.pathname.replace('/', '');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (url.hostname.includes('youtube.com')) {
        // watch?v=ID or embed/ID
        const v = url.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}`;
        const parts = url.pathname.split('/').filter(Boolean);
        const idx = parts.indexOf('embed');
        if (idx >= 0 && parts[idx + 1]) return `https://www.youtube.com/embed/${parts[idx + 1]}`;
      }
    } catch {}
    return '';
  };

  const handleReset = () => {
    setCourse({
      courseId: '',
      title: '',
      punchline: '',
      shortDescription: '',
      description: '',
      price: '',
      instructor: '',
      instructor2: '',
      location: '',
      startDate: '',
      endDate: '',
      targetgroup: '',
      partnerId: '',
      partner2Id: '',
      isExternalRegistration: false,
      externalRegistrationUrl: '',
      isActive: true,
      discountInformation: '',
    });
    setMessage(null);
    updateSessionsCount(1);
  };

  // Inject selector into header
  React.useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="courseSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
          ${saving ? 'disabled' : ''}
        >
          <option value="" ${selectedCourseId === '' ? 'selected' : ''}>Create New Course</option>
          ${availableCourses.map(course => {
            return `<option value="${course.id}" ${selectedCourseId === course.id ? 'selected' : ''}>${course.courseId} - ${course.title} - ${course.location}</option>`;
          }).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handleCourseSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedCourseId, availableCourses, availableTutors, saving]);

  if (loading) {
    return (
      <div className="admin-content-card">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading course...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-card admin-course-editor admin-editor-content">
      {/* Saving Overlay */}
      {saving && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          cursor: 'wait'
        }}>
          <div style={{
            background: 'white',
            padding: '24px 32px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #008F99',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
              Saving course...
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Please do not close this window
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <form onSubmit={handleSubmit} style={{ padding: '24px', pointerEvents: saving ? 'none' : 'auto', opacity: saving ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="courseId">Course ID</label>
            <input
              type="text"
              id="courseId"
              name="courseId"
              value={course.courseId || ''}
              onChange={handleInputChange}
              placeholder="e.g., AI-001, FUND-2024 (optional, admin-managed)"
            />

          </div>

          <div className="form-field">
            <label htmlFor="instructor2">Second Instructor (optional)</label>
            <select
              id="instructor2"
              name="instructor2"
              value={course.instructor2 || ''}
              onChange={handleInputChange}
              disabled={!supportsTutor2}
            >
              <option value="">Select a second instructor (optional)</option>
              {availableTutors.map((tutor) => (
                <option key={tutor.id} value={tutor.id}>
                  {tutor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="title">Course Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={course.title || ''}
              onChange={handleInputChange}
              placeholder="e.g., AI Fundamentals Workshop"
            />
          </div>

          <div className="form-field">
            <label htmlFor="instructor">Instructor</label>
            <select
              id="instructor"
              name="instructor"
              value={course.instructor || ''}
              onChange={handleInputChange}
            >
              <option value="">Select an instructor (optional)</option>
              {availableTutors.map((tutor) => (
                <option key={tutor.id} value={tutor.id}>
                  {tutor.name}
                </option>
              ))}
            </select>
            
          </div>

          <div className="form-field">
            <label htmlFor="partnerId">Partner Organization</label>
            <select
              id="partnerId"
              name="partnerId"
              value={course.partnerId || ''}
              onChange={handleInputChange}
            >
              <option value="">Select a partner (optional)</option>
              {availablePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>

          </div>

          <div className="form-field">
            <label htmlFor="partner2Id">Second Partner (optional)</label>
            <select
              id="partner2Id"
              name="partner2Id"
              value={course.partner2Id || ''}
              onChange={handleInputChange}
            >
              <option value="">Select a second partner (optional)</option>
              {availablePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="price">Price</label>
            <input
              type="text"
              id="price"
              name="price"
              value={course.price || ''}
              onChange={handleInputChange}
              placeholder="e.g., 1,200 €"
            />
          </div>










          <div className="form-field">
            <label htmlFor="targetgroup">Target Group</label>
            <input
              type="text"
              id="targetgroup"
              name="targetgroup"
              value={course.targetgroup || ''}
              onChange={handleInputChange}
              placeholder="e.g., Business professionals, Developers"
            />
          </div>


        </div>

        {/* Description Collapsible Section */}
        <CollapsibleSection 
          title="Description" 
          isOpen={collapsibleStates.description}
          onToggle={() => toggleCollapsibleSection('description')}
        >
          <div className="form-field">
            <label htmlFor="punchline">Course Punchline</label>
            <input
              type="text"
              id="punchline"
              name="punchline"
              value={course.punchline || ''}
              onChange={handleInputChange}
              placeholder="e.g., Master AI fundamentals in 2 days"
            />
          </div>

          <div className="form-field">
            <label htmlFor="shortDescription">Course Short Description</label>
            <textarea
              id="shortDescription"
              name="shortDescription"
              value={course.shortDescription || ''}
              onChange={handleInputChange}
              rows={2}
              placeholder="Brief overview of the course (1-2 sentences)"
            />
          </div>

          <div className="form-field">
            <label htmlFor="description">Course Description</label>
            <div className="quill-editor-container">
              <ReactQuill
                theme="snow"
                value={course.description || ''}
                onChange={handleDescriptionChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe what participants will learn in this course..."
                style={{ minHeight: '200px' }}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Settings Collapsible Section */}
        <CollapsibleSection 
          title="Settings" 
          isOpen={collapsibleStates.settings}
          onToggle={() => toggleCollapsibleSection('settings')}
        >
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="urlSlug">
                URL Slug *
              </label>
              <input
                type="text"
                id="urlSlug"
                name="urlSlug"
                value={(() => {
                  console.log('🔍 Rendering URL Slug input with value:', course.urlSlug);
                  console.log('🔍 Current course state:', JSON.stringify(course, null, 2));
                  return course.urlSlug || '';
                })()}
                onChange={(e) => {
                  console.log('🔍 URL Slug input changed to:', e.target.value);
                  handleInputChange(e);
                }}
                placeholder="e.g., ai-fundamentals-2024"
                pattern="[a-z0-9\-]+"
                title="Only lowercase letters, numbers, and hyphens allowed"
              />
            </div>

            <div className="form-field">
              <label htmlFor="isExternalRegistration">External Registration</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  id="isExternalRegistration"
                  name="isExternalRegistration"
                  checked={!!course.isExternalRegistration}
                  onChange={handleInputChange}
                />
                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  Use external partner registration instead of built-in form
                </span>
              </div>
            </div>

            {course.isExternalRegistration && (
              <div className="form-field">
                <label htmlFor="externalRegistrationUrl">External Registration URL</label>
                <input
                  type="url"
                  id="externalRegistrationUrl"
                  name="externalRegistrationUrl"
                  value={course.externalRegistrationUrl || ''}
                  onChange={handleInputChange}
                  placeholder="https://partner.example.com/register"
                />
              </div>
            )}

            {/* Discount Information */}
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="discountInformation">Rabatt-Informationen</label>
              <div className="quill-editor-container">
                <ReactQuill
                  theme="snow"
                  value={course.discountInformation || ''}
                  onChange={handleDiscountChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="z.B. 50€ Rabatt für Partner-Mitarbeiter, 100€ Frühbucherrabatt bis 31.12., etc."
                  style={{ minHeight: '150px' }}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Media Collapsible Section */}
        <CollapsibleSection 
          title="Media" 
          isOpen={collapsibleStates.media}
          onToggle={() => toggleCollapsibleSection('media')}
        >
          <div className="form-field">
            <label>Hero Background Image</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {heroPreviewUrl && (
                <img src={heroPreviewUrl} alt="Hero preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
              )}
              {/* Existing images selector */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={course.heroImagePath || ''}
                  onChange={async (e) => {
                    const selectedKey = e.target.value;
                    setCourse(prev => ({ ...prev, heroImagePath: selectedKey || '' }));
                  }}
                >
                  <option value="">Choose existing hero image…</option>
                  {existingHeroItems.map(item => (
                    <option key={item.key} value={item.key}>{item.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="admin-btn"
                  onClick={loadExistingHeroImages}
                  disabled={loadingExistingHeroes}
                >
                  {loadingExistingHeroes ? 'Refreshing…' : 'Refresh list'}
                </button>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setHeroFile(f);
                  if (f) {
                    const localUrl = URL.createObjectURL(f);
                    setHeroPreviewUrl(localUrl);
                  }
                }}
              />
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="admin-btn"
                  disabled={!heroFile || heroUploading}
                  onClick={async () => {
                    if (!heroFile) return;
                    try {
                      setHeroUploading(true);
                      const safeSlug = (course.urlSlug || course.title || 'course')
                        .toLowerCase()
                        .replace(/[^a-z0-9\-]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      const timestamp = Date.now();
                      // Do NOT prefix with 'public/' here; Amplify Storage will add it for the public access level
                      const key = `public/courses/${safeSlug}/${timestamp}_${heroFile.name}`;
                      await uploadData({ key, data: heroFile, options: { contentType: heroFile.type } }).result;
                      const { url } = await getUrl({ path: key });
                      setCourse(prev => ({ ...prev, heroImagePath: key }));
                      setHeroPreviewUrl(url.toString());
                      setMessage({ type: 'success', text: 'Hero image uploaded successfully.' });
                      // refresh list so the new upload appears
                      loadExistingHeroImages();
                    } catch (err:any) {
                      console.error('Upload failed:', err);
                      setMessage({ type: 'error', text: `Hero upload failed: ${err?.message || err}` });
                    } finally {
                      setHeroUploading(false);
                    }
                  }}
                >
                  {heroUploading ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </div>
          </div>

          {/* YouTube Videos */}
          <div className="form-section" style={{ marginTop: '1.5rem' }}>
            <h3>YouTube Videos</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Add up to 10 videos. Choose left (primary) or right (secondary) column.</div>
              <button
                type="button"
                className="admin-btn"
                disabled={videos.length >= 10}
                onClick={() => {
                  setVideos(prev => ([...prev, { url: '', heading: '', isPrimary: true, order: prev.length, isActive: true }]));
                }}
              >
                + Add Video
              </button>
            </div>

            {videos.length === 0 && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px', color: '#6b7280' }}>
                No videos added yet.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {videos.map((v, idx) => {
                const embed = extractYouTubeEmbedUrl(v.url || '');
                return (
                  <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px', background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong style={{ color: '#374151' }}>Video {idx + 1}</strong>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="admin-btn"
                          disabled={idx === 0}
                          onClick={() => {
                            if (idx === 0) return;
                            setVideos(prev => {
                              const copy = [...prev];
                              const tmp = copy[idx - 1];
                              copy[idx - 1] = copy[idx];
                              copy[idx] = tmp;
                              return copy.map((it, i) => ({ ...it, order: i }));
                            });
                          }}
                        >↑</button>
                        <button
                          type="button"
                          className="admin-btn"
                          disabled={idx === videos.length - 1}
                          onClick={() => {
                            if (idx === videos.length - 1) return;
                            setVideos(prev => {
                              const copy = [...prev];
                              const tmp = copy[idx + 1];
                              copy[idx + 1] = copy[idx];
                              copy[idx] = tmp;
                              return copy.map((it, i) => ({ ...it, order: i }));
                            });
                          }}
                        >↓</button>
                        <button
                          type="button"
                          className="admin-btn"
                          onClick={() => {
                            setVideos(prev => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i })));
                          }}
                        >Remove</button>
                      </div>
                    </div>

                    <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="form-field">
                        <label>Heading</label>
                        <input
                          type="text"
                          value={v.heading || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setVideos(prev => prev.map((it, i) => i === idx ? { ...it, heading: value } : it));
                          }}
                          placeholder="e.g., Why this course matters"
                        />
                      </div>
                      <div className="form-field">
                        <label>YouTube URL</label>
                        <input
                          type="url"
                          value={v.url || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setVideos(prev => prev.map((it, i) => i === idx ? { ...it, url: value } : it));
                          }}
                          placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                        />
                      </div>
                    </div>

                    <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="form-field">
                        <label>Column</label>
                        <select
                          value={v.isPrimary ? 'primary' : 'secondary'}
                          onChange={(e) => {
                            const next = e.target.value === 'primary';
                            setVideos(prev => prev.map((it, i) => i === idx ? { ...it, isPrimary: next } : it));
                          }}
                        >
                          <option value="primary">Left (primary)</option>
                          <option value="secondary">Right (secondary)</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Active</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            type="checkbox"
                            checked={v.isActive ?? true}
                            onChange={(e) => {
                              const checked = (e.target as HTMLInputElement).checked;
                              setVideos(prev => prev.map((it, i) => i === idx ? { ...it, isActive: checked } : it));
                            }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Visible on course page</span>
                        </div>
                      </div>
                    </div>

                    {embed && (
                      <div style={{ marginTop: 8 }}>
                        <div className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          <iframe
                            src={embed}
                            title={`YouTube video ${idx + 1}`}
                            frameBorder={0}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleSection>

        {/* Location and Dates Collapsible Section */}
        <CollapsibleSection 
          title="Location and Dates" 
          isOpen={collapsibleStates.locationAndDates}
          onToggle={() => toggleCollapsibleSection('locationAndDates')}
        >
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={course.location || ''}
                onChange={handleInputChange}
                placeholder="e.g., Munich, Online, Hybrid"
              />
            </div>

            <div className="form-field">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={course.startDate || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-field">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={course.endDate || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Sessions Collapsible Section */}
        <CollapsibleSection 
          title="Sessions" 
          isOpen={collapsibleStates.sessions}
          onToggle={() => toggleCollapsibleSection('sessions')}
        >
          {/* Integrated Session Management */}
          <div className="session-management">
            <div className="session-header-controls">
              <div className="session-count-selector">
                <label htmlFor="numberOfSessions">Sessions</label>
                <select
                  id="numberOfSessions"
                  value={numberOfSessions}
                  onChange={(e) => updateSessionsCount(parseInt(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="session-tiles">
                {sessions.map((session, index) => (
                  <div 
                    key={index} 
                    className={`session-tile ${index === currentSessionIndex ? 'active' : ''}`}
                    onClick={() => setCurrentSessionIndex(index)}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Current Session Editor */}
          {sessions[currentSessionIndex] && (
            <div className="session-editor">
              <div className="session-header">
                <h4>Session {currentSessionIndex + 1} Details</h4>
              </div>

              <div className="session-form">
                <div className="session-row">
                  <div className="form-field">
                    <label>Session Title</label>
                    <input
                      type="text"
                      value={sessions[currentSessionIndex]?.title || ''}
                      onChange={(e) => updateSession(currentSessionIndex, 'title', e.target.value)}
                      placeholder={`Session ${currentSessionIndex + 1}`}
                    />
                  </div>
                  
                  <div className="form-field">
                    <label>Date</label>
                    <input
                      type="date"
                      value={sessions[currentSessionIndex]?.date || ''}
                      onChange={(e) => updateSession(currentSessionIndex, 'date', e.target.value)}
                    />
                  </div>
                </div>

                <div className="session-row">
                  <div className="form-field">
                    <label>
                      Start Time
                      {currentSessionIndex > 0 && (
                        <button
                          type="button"
                          className="copy-btn"
                          onClick={() => copyFromPreviousSession('startTime')}
                          title="Copy start time from previous session"
                        >
                          📋
                        </button>
                      )}
                    </label>
                    <input
                      type="time"
                      value={sessions[currentSessionIndex]?.startTime || '09:00'}
                      onChange={(e) => updateSession(currentSessionIndex, 'startTime', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-field">
                    <label>
                      End Time
                      {currentSessionIndex > 0 && (
                        <button
                          type="button"
                          className="copy-btn"
                          onClick={() => copyFromPreviousSession('endTime')}
                          title="Copy end time from previous session"
                        >
                          📋
                        </button>
                      )}
                    </label>
                    <input
                      type="time"
                      value={sessions[currentSessionIndex]?.endTime || '17:00'}
                      onChange={(e) => updateSession(currentSessionIndex, 'endTime', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>
                    Location Type
                    {currentSessionIndex > 0 && (
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => copyFromPreviousSession('locationType')}
                        title="Copy location type from previous session"
                      >
                        📋
                      </button>
                    )}
                  </label>
                  <select
                    value={sessions[currentSessionIndex]?.locationType || 'ON_SITE'}
                    onChange={(e) => updateSession(currentSessionIndex, 'locationType', e.target.value as 'ONLINE' | 'ON_SITE')}
                  >
                    <option value="ON_SITE">On-Site (Physical Location)</option>
                    <option value="ONLINE">Online (Virtual Session)</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>
                    {sessions[currentSessionIndex]?.locationType === 'ONLINE' ? 'Session Link/Platform' : 'Physical Address'}
                    {currentSessionIndex > 0 && (
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => copyFromPreviousSession('location')}
                        title="Copy location from previous session"
                      >
                        📋
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    value={sessions[currentSessionIndex]?.location || ''}
                    onChange={(e) => updateSession(currentSessionIndex, 'location', e.target.value)}
                    placeholder={
                      sessions[currentSessionIndex]?.locationType === 'ONLINE' 
                        ? 'e.g., Zoom, Teams, or session link'
                        : 'e.g., Conference Room A, Training Center'
                    }
                  />
                </div>

                <div className="form-field">
                  <label>Session Description</label>
                  <textarea
                    value={sessions[currentSessionIndex]?.description || ''}
                    onChange={(e) => updateSession(currentSessionIndex, 'description', e.target.value)}
                    rows={2}
                    placeholder="What will be covered in this session?"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sessions Preview Section */}
          {sessions.length > 0 && sessions.some(s => s.title && s.date) && (
            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ color: '#374151', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600 }}>
                Public Course Page Preview
              </h4>
              <div className="sessions-preview">
                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  How sessions will appear on the public course page:
                </p>
                
                <div className="sessions-detailed">
                  <div className="sessions-list">
                    {sessions
                      .filter(session => session.title && session.date)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((session, index) => (
                      <div key={index} className="session-card">
                        <div className="session-header">
                          <div className="session-number">Session {index + 1}</div>
                          <h4>{session.title}</h4>
                          <span className={`session-type ${session.locationType?.toLowerCase()}`}>
                            {session.locationType === 'ONLINE' ? '🌐 Online' : '📍 On-site'}
                          </span>
                        </div>
                        
                        <div className="session-details">
                          <div className="session-date">
                            <strong>Date:</strong> {session.date}
                          </div>
                          <div className="session-time">
                            <strong>Time:</strong> {session.startTime} - {session.endTime}
                          </div>
                          {session.locationType !== 'ONLINE' && session.location && (
                            <div className="session-location">
                              <strong>Location:</strong> {session.location}
                            </div>
                          )}
                        </div>
                        
                        {session.description && (
                          <div className="session-description">
                            <p>{session.description}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CollapsibleSection>

        <div className="form-actions">
          <button
            type="button"
            className="admin-btn"
            onClick={handleReset}
            disabled={saving}
          >
            Reset
          </button>
        </div>
      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
        onVisit={() => {
          if (course.urlSlug) {
            navigate(`/courses/${course.urlSlug}`);
          }
        }}
        visitDisabled={!course.urlSlug}
        visitTitle={course.urlSlug ? `Visit /courses/${course.urlSlug}` : 'Set URL Slug to enable'}
      />
    </div>
  );
};



export default AdminCourseEditor;